import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import Config from 'config';
import { getYear, isEqual } from 'date-fns';
import useStore from 'store';
import useUiStore from 'store/ui';
import GET_ACADEMIC_SESSIONS from 'graph/academic-session/academic-sessions-query';
import REMOVE_UPCOMING_ACADEMIC_SESSION_SETTING from 'graph/academic-session/remove-upcoming-academic-session-setting';
import UPDATE_ACADEMIC_SESSION from 'graph/academic-session/update-academic-session';
import UPDATE_UPCOMING_ACADEMIC_SESSION_SETTING from 'graph/academic-session/update-upcoming-academic-session-setting';
import { AlertVariant } from 'types/enums';
import {
  AcademicSession,
  Organization,
  OrganizationSetting,
  SourceKind
} from 'types/schema-generated-types';
import EndOfYearProcessingModal from '../EndOfYearProcessingModal';
import { EndOfYearProcessingFormValues, integrationTypes, UseEndOfYearProcessing } from '../types';
import { getActiveAcademicSession, getNotificationType } from '../utils';

const { privilegesGroups } = Config;

const isAutomaticYearEndOrganization = (organization?: Organization): boolean => {
  const sourceKind = organization?.externalId?.sourceKind;
  return sourceKind ? [SourceKind.CLASS_LINK, SourceKind.CLEVER].includes(sourceKind) : false;
};

const useEndOfYearProcessing = (): UseEndOfYearProcessing => {
  const { t } = useTranslation('EndOfYearProcessing');
  const [, { closeModal }] = useUiStore();
  const [{ user, userPrivilegesCache }, { openQuarterModal, openAlert }] = useStore();

  const [updateAcademicSession] = useMutation(UPDATE_ACADEMIC_SESSION);
  const [updateUpcomingAcademicSession] = useMutation(UPDATE_UPCOMING_ACADEMIC_SESSION_SETTING);
  const [removeUpcomingAcademicSession] = useMutation(REMOVE_UPCOMING_ACADEMIC_SESSION_SETTING);
  const [loadAcademicSessions, { data: academicSessionsData }] = useLazyQuery<{
    academicSessions: AcademicSession[];
    organizationSetting: OrganizationSetting | null;
  }>(GET_ACADEMIC_SESSIONS, { fetchPolicy: 'cache-and-network' });

  const [values, setValues] = useState<EndOfYearProcessingFormValues>({
    dateTermEnd: undefined,
    upcomingDateTermStart: undefined,
    upcomingIntegrationType: undefined
  });

  const parentOrganization: Organization | undefined =
    (userPrivilegesCache.isPrivileged(privilegesGroups.mutateAcademicSession) &&
      user.me?.organizationAssociations?.find(item => !item.organization?.parentOrganization)
        ?.organization) ||
    undefined;

  const notificationType = useMemo(() => {
    if (isAutomaticYearEndOrganization(parentOrganization)) {
      return getNotificationType(academicSessionsData?.academicSessions ?? []);
    }

    return undefined;
  }, [academicSessionsData, parentOrganization]);

  const openModal = () =>
    openQuarterModal({
      body: <EndOfYearProcessingModal />
    });

  const handleCurrentAcademicSessionUpdate = async (updatedDateTermEnd: Date | undefined) => {
    if (!updatedDateTermEnd) return;

    const { id, dateTermEnd } = values;
    if (id && dateTermEnd) {
      if (!isEqual(new Date(dateTermEnd), new Date(updatedDateTermEnd))) {
        await updateAcademicSession({
          variables: { data: { dateTermEnd: updatedDateTermEnd }, where: { id } }
        });
      }
    }
  };

  const handleUpcomingAcademicSessionMutation = async (
    updatedUpcomingDateTermStart: Date | undefined,
    updatedUpcomingIntegrationType: string | undefined
  ) => {
    if (!parentOrganization?.id) return;

    if (!updatedUpcomingDateTermStart && parentOrganization) {
      removeUpcomingAcademicSession({ variables: { organizationId: parentOrganization.id } });
      return;
    }

    const value = {
      ...(updatedUpcomingDateTermStart && {
        dateTermStart: updatedUpcomingDateTermStart,
        dateTermEnd: new Date(`25 July ${getYear(updatedUpcomingDateTermStart) + 1}`)
      }),
      ...(updatedUpcomingIntegrationType && { sourceKind: updatedUpcomingIntegrationType })
    };

    updateUpcomingAcademicSession({ variables: { organizationId: parentOrganization.id, value } });
  };

  const handleUpdate = async ({
    dateTermEnd,
    upcomingDateTermStart,
    upcomingIntegrationType
  }: EndOfYearProcessingFormValues): Promise<void> => {
    await handleCurrentAcademicSessionUpdate(dateTermEnd);
    await handleUpcomingAcademicSessionMutation(upcomingDateTermStart, upcomingIntegrationType);

    closeModal();

    openAlert({
      variant: AlertVariant.SUCCESS,
      title: t('End of Year Processing Updated'),
      text: t('Your End of Year Processing details have been successfully updated.')
    });
  };

  useEffect(() => {
    if (academicSessionsData?.academicSessions) {
      const { academicSessions } = academicSessionsData;
      const activeAcademicSession = getActiveAcademicSession(academicSessions);

      setValues(values => ({
        ...values,
        id: activeAcademicSession?.id,
        dateTermEnd: activeAcademicSession?.dateTermEnd
      }));
    }

    if (academicSessionsData?.organizationSetting) {
      const { value } = academicSessionsData.organizationSetting;

      setValues(values => ({
        ...values,
        upcomingDateTermStart: value?.dateTermStart ? new Date(value.dateTermStart) : undefined,
        upcomingDateTermEnd: value?.dateTermEnd ? new Date(value.dateTermEnd) : undefined,
        upcomingIntegrationType: integrationTypes.find(type => type.value === value?.sourceKind)
          ?.value
      }));
    }
  }, [academicSessionsData]);

  useEffect(() => {
    if (parentOrganization) {
      loadAcademicSessions({
        variables: {
          where: { organizationId: parentOrganization.id, active: true, default: false },
          organizationId: parentOrganization.id
        }
      });
    }
  }, [parentOrganization, loadAcademicSessions]);

  return {
    notificationType,
    values,
    openModal,
    handleUpdate
  };
};

export default useEndOfYearProcessing;
